import axios from 'axios';
import { getCurrentUser, setCurrentUser } from '../helpers/Utils';

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_BASE_URL}/api`,
});

// Evita /api/api/... quando REACT_APP_BACKEND_BASE_URL e' vazio (mesma origem) e a tela monta a URL com `${api}/...`
// (constants/defaultValues.js ja inclui o prefixo): a URL que ja comeca pelo baseURL relativo dispensa o baseURL.
api.interceptors.request.use((config) => {
  if (
    config.baseURL &&
    typeof config.url === 'string' &&
    !/^https?:\/\//.test(config.baseURL) &&
    config.url.startsWith(`${config.baseURL}/`)
  ) {
    return { ...config, baseURL: '' };
  }
  return config;
});

export const LOGIN_PATH = '/user/login';
// Parâmetro de rota que a tela de login lê para avisar "Sessão expirada" (F-07).
// Só um sinal booleano na URL: nunca carrega token nem dado da sessão.
export const SESSION_EXPIRED_PARAM = 'expired';
export const SESSION_EXPIRED_PATH = `${LOGIN_PATH}?${SESSION_EXPIRED_PARAM}=1`;
export const LOGOUT_URL = '/auth/logout/';

// O login é o único endpoint que NÃO deve levar o token: um token antigo no
// header faz o DRF responder 401 "Invalid token" ao próprio POST de login (F-20).
const isLoginRequest = (config) =>
  !!config &&
  typeof config.url === 'string' &&
  /auth_token\/?$/.test(config.url);

// O logout é uma chamada "best effort": um 401 nela (token já expirado) não deve
// virar o aviso de "sessão expirada" para quem acabou de clicar em Sair.
const isLogoutRequest = (config) =>
  !!config &&
  typeof config.url === 'string' &&
  /auth\/logout\/?$/.test(config.url);

// Remove o header de autorização "residual" de `api.defaults` (herdado das
// atribuições manuais antigas ou de uma sessão anterior). Chamado no logout e
// quando o servidor responde 401.
export const clearAuthorizationHeader = () => {
  delete api.defaults.headers.Authorization;
  if (api.defaults.headers.common) {
    delete api.defaults.headers.common.Authorization;
  }
};

// Request: injeta o token da sessão atual em toda requisição, lido a cada
// chamada (sempre o token vigente, mesmo após logout/login de outro usuário).
api.interceptors.request.use((config) => {
  const nextConfig = config;
  nextConfig.headers = nextConfig.headers || {};
  if (isLoginRequest(nextConfig)) {
    delete nextConfig.headers.Authorization;
    if (nextConfig.headers.common) {
      delete nextConfig.headers.common.Authorization;
    }
    return nextConfig;
  }
  const user = getCurrentUser();
  if (user && user.token) {
    nextConfig.headers.Authorization = `Token ${user.token}`;
  }
  return nextConfig;
});

// Sessão rejeitada pelo servidor (token inválido/expirado/revogado): limpa a
// sessão local e leva ao login, que mostra "Sessão expirada, entre novamente". `window.location.assign` (e não o history do
// react-router) porque este módulo não tem acesso ao router - e o reload
// completo também zera o estado do redux e os caches em memória.
export const handleUnauthorized = () => {
  setCurrentUser();
  clearAuthorizationHeader();
  const path = window.location.pathname || '';
  if (path !== LOGIN_PATH && path.indexOf('/user/') !== 0) {
    window.location.assign(SESSION_EXPIRED_PATH);
  }
};

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error && error.response && error.response.status;
    if (
      status === 401 &&
      !isLoginRequest(error.config) &&
      !isLogoutRequest(error.config)
    ) {
      handleUnauthorized();
    }
    return Promise.reject(error);
  }
);

export default api;
